import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, of, take } from 'rxjs';
import { SessionChangeEvent, SessionChangeEventType, LoggingService, ProjectService } from '@newroom-connect/library/services';

/**
 * Service responsible for handling navigation based on session and project changes.
 * Listens to session events and performs appropriate navigation actions.
 */
@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  private static readonly PUBLIC_ROUTES = ['/signin', '/legal', '/404'];

  private readonly router = inject(Router);
  private readonly projectService = inject(ProjectService);
  private readonly loggingService = inject(LoggingService);

  /**
   * Navigate to the sign-in page for a specific project.
   *
   * @param projectSlug Optional project slug to include in the route, defaults to 404 if not provided.
   */
  public navigateToSignIn(projectSlug?: string | null): void {
    if (!projectSlug) {
      this.navigateTo404();
      return;
    }

    const routingParams = [projectSlug, 'signin'];

    this.router.navigate(routingParams).catch(error =>
      this.loggingService.error(`Failed to navigate to ${JSON.stringify(routingParams)}`, error)
    );
  }

  /**
   * Navigate to the areas page for a specific project.
   *
   * @param projectSlug
   * @param areaSlug Optional area slug to navigate to a specific area.
   */
  public navigateToAreas(projectSlug: string, areaSlug?: string): void {
    const route = areaSlug ? [projectSlug, 'areas', areaSlug] : [projectSlug, 'areas'];

    this.router.navigate(route).catch(error =>
      this.loggingService.error('Failed to navigate to areas:', error)
    );
  }

  /**
   * Navigate to the main area after successful login.
   * Uses declarative priority system for determining target area.
   *
   * @param projectSlug
   */
  public navigateToMainArea(projectSlug: string): void {
    this.projectService.getProjectSigninConfig(projectSlug)
      .pipe(
        take(1),
        catchError(error => {
          this.loggingService.error('Failed to get project config for navigation:', error);
          this.navigateTo404();
          return of(null);
        })
      )
      .subscribe({
        next: (config) => {
          // Use startAreaSlug from project config if available.
          if (config?.startAreaSlug) {
            this.navigateToAreas(projectSlug, config.startAreaSlug);
          } else {
            this.navigateToAreas(projectSlug);
          }
        },
        error: (error) => {
          this.loggingService.error('Failed to get project config for navigation:', error);
          // Fallback to default areas route
          this.navigateToAreas(projectSlug);
        }
      });
  }

  /**
   * Navigate to a clean URL by removing query parameters.
   * Useful for cleaning up URLs after processing query parameters like refreshToken.
   *
   * @param url The current URL that may contain query parameters.
   */
  public navigateToCleanUrl(url: string): void {
    const cleanUrl = url.split('?')[0];

    this.router.navigateByUrl(cleanUrl).catch(error =>
      this.loggingService.error(`Failed to navigate to clean URL: ${cleanUrl}`, error)
    );
  }

  /**
   * Handle session change events and perform appropriate navigation.
   *
   * @param event
   */
  public handleSessionChange(event: SessionChangeEvent): void {
    switch (event.type) {
      case SessionChangeEventType.SESSION_CLEARED:
        if (!this.isOnPublicRoute()) {
          this.navigateToSignIn(this.getCurrentProjectSlug());
        }

        break;

      case SessionChangeEventType.SESSION_SET:
      case SessionChangeEventType.SESSION_INITIALIZED:
        break;

      default:
        // Handle any future session change types
        break;
    }
  }

  /**
   * Navigate to the 404 page.
   */
  public navigateTo404(): void {
    this.router.navigate(['/404']).catch(error =>
      this.loggingService.error('Failed to navigate to 404 page:', error)
    );
  }

  /**
   * Check if the current route is a public route that doesn't require authentication.
   *
   * @returns True if currently on a public route.
   */
  private isOnPublicRoute(): boolean {
    const url = this.router.url;

    return NavigationService.PUBLIC_ROUTES.some(route => url.includes(route));
  }

  /**
   * Extract the project slug from the current URL.
   *
   * @returns The project slug or undefined if not found.
   */
  private getCurrentProjectSlug(): string | undefined {
    const urlSegments = this.router.url.split('/');

    // Assuming URL structure: /projectSlug/...
    return urlSegments.length > 1 ? urlSegments[1] : undefined;
  }
}
